import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { selectSelectedBilledJob, selectSelectedBilledJobLoading } from "../../redux/billedJobs/billedJobsSlice";
import { closeModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import moment from "moment";

export default function ViewBilledJob() {
    const dispatch = useDispatch();
    const selectedJob = useSelector(selectSelectedBilledJob);
    const selectedJobLoading = useSelector(selectSelectedBilledJobLoading);
    
    // Formatta il prezzo
    const formatPrice = (price) => {
        if (price === undefined || price === null) return "N/A";
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(parseFloat(price));
    };
    
    // Calcola il totale della commessa (somma degli item)
    const getTotalAmount = () => {
        if (!selectedJob?.items || !Array.isArray(selectedJob.items)) return 0;
        return selectedJob.items.reduce((acc, item) => acc + (parseFloat(item.totalPrice) || 0), 0);
    };
    
    if (selectedJobLoading) {
        return <LoadingSection />;
    }
    
    if (!selectedJob) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Commessa non trovata</h2>
                <p className="text-gray-600">La commessa richiesta non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <div className="flex items-center space-x-3">
                        <h2 className="text-2xl font-bold">Commessa {selectedJob.code}</h2>
                        <span className="px-2 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                            Fatturata
                        </span>
                    </div>
                    {selectedJob.creationDate && (
                        <p className="text-gray-600">
                            Data: {moment(selectedJob.creationDate).format('DD/MM/YYYY')}
                        </p>
                    )}
                </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Informazioni Generali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedJob.customerLabel && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Cliente</h4>
                            <p className="text-base">{selectedJob.customerLabel}</p>
                        </div>
                    )}
                    
                    {selectedJob.statusLabel && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Stato</h4>
                            <p className="text-base">{selectedJob.statusLabel}</p>
                        </div>
                    )}
                    
                    {selectedJob.jobTypeLabel && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Tipo</h4>
                            <p className="text-base">{selectedJob.jobTypeLabel}</p>
                        </div>
                    )}
                    
                    {selectedJob.description && (
                        <div className="col-span-1 md:col-span-2">
                            <h4 className="text-sm font-medium text-gray-500">Descrizione</h4>
                            <p className="text-base whitespace-pre-line">{selectedJob.description}</p>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Articoli */}
            {selectedJob.items && selectedJob.items.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3 border-b pb-2">Articoli</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Prodotto/Servizio
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Quantità
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Prezzo Unitario
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Totale
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {selectedJob.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {item.productLabel || item.serviceLabel || "Articolo senza nome"}
                                            </div>
                                            {item.description && (
                                                <div className="text-sm text-gray-500">
                                                    {item.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.quantity || 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatPrice(item.unitPrice)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatPrice(item.totalPrice)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 text-right">
                        <p className="text-lg font-semibold">
                            Totale: {formatPrice(getTotalAmount())}
                        </p>
                    </div>
                </div>
            )}
            
            {/* Informazioni di pagamento */}
            {selectedJob.paymentTypeLabel && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3 border-b pb-2">Informazioni di Pagamento</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h4 className="text-sm font-medium text-gray-500">Metodo di Pagamento</h4>
                            <p className="text-base">{selectedJob.paymentTypeLabel}</p>
                        </div>
                        
                        {selectedJob.paymentTypeBankLabel && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Banca</h4>
                                <p className="text-base">{selectedJob.paymentTypeBankLabel}</p>
                            </div>
                        )}
                        
                        {selectedJob.globalTotalPrice && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Totale Imponibile</h4>
                                <p className="text-base">{formatPrice(selectedJob.globalTotalPrice)}</p>
                            </div>
                        )}
                        
                        {selectedJob.globalTotalPriceVatted && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Totale con IVA</h4>
                                <p className="text-base">{formatPrice(selectedJob.globalTotalPriceVatted)}</p>
                            </div>
                        )}
                    </div>
                    
                    {/* Pagamenti */}
                    {selectedJob.payments && selectedJob.payments.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">Pagamenti</h4>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Data
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Importo
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Stato
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedJob.payments.map((payment, index) => (
                                            <tr key={index}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {payment.date ? moment(payment.date).format('DD/MM/YYYY') : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatPrice(payment.value)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {payment.status === 'paid' ? 'Pagato' :
                                                         payment.status === 'pending' ? 'In Attesa' :
                                                         payment.status || 'N/A'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>
        </div>
    );
}
